import { useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/services/firebase';
import {
  p2pThreadId,
  subscribeP2PLatestPeerMessage,
  subscribeP2PThreadMeta,
} from '@/services/chatMessagesFirestore';
import { computeMyReadThroughMessageTs } from '@/utils/chatPeerReadReceipt';
import i18n from '@/i18n';
import { showChatIncomingMessageNotification } from '@/services/chatBrowserNotifications';
import { readChatBrowserNotifyEnabled } from '@/services/chatBrowserNotificationsPrefs';
import { playIncomingMessageSound } from '@/services/incomingMessageSound';
import { buildNotificationBodyFromRawForInbox } from '@/services/chatNotificationBodyPreview';

type LatestPeer = { id: string; createdAtMs: number } | null;

/** Разделитель для ключа списка peer (токены не содержат \u001e). */
const PEER_KEY_SEP = '\u001e';

/** Сколько мс подряд «есть непрочитанное» в расчёте — только тогда показываем точку (отсекает гонки снимков). */
const UNREAD_DOT_SHOW_AFTER_MS = 900;
/** Сколько мс подряд «нет непрочитанного» — убираем точку (без мгновенного мигания при кратком true). */
const UNREAD_DOT_HIDE_AFTER_MS = 450;

/**
 * Для каждого peer-токена — есть ли входящее от собеседника новее метки «прочитано» в Firestore (`readThroughTs*`).
 */
export function useChatUnreadByPeers(params: {
  uid: string;
  myInviteToken: string | null | undefined;
  peerTokens: string[];
  /** Сброс пересчёта (локальный режим чата / миграция read-receipt). */
  readReceiptVersion: number;
  /**
   * Peer-токен открытого чата (из URL). Для этого треда не играем звук и не шлём уведомление из inbox-хука:
   * лента уже подписана в `ChatThread` (`useFirestoreP2PChat`) — там один сценарий звука/уведомления при неактивной вкладке.
   * Caller must omit this while PIN gate / `blocksCalls` is active (ChatThread unmounted).
   */
  suppressIncomingSoundPeerToken?: string | undefined;
  /** Метаданные уведомления (имя, фото) по peer-токену. */
  getPeerNotifyMeta?: (
    peerToken: string
  ) => { title?: string; iconUrl?: string | null } | undefined;
  /** uid собеседника из контакта (если уже известен) — для расшифровки превью уведомления. */
  getPeerUid?: (peerToken: string) => string | null | undefined;
}): Record<string, boolean> {
  const {
    uid,
    myInviteToken,
    peerTokens,
    readReceiptVersion,
    suppressIncomingSoundPeerToken,
    getPeerNotifyMeta,
    getPeerUid,
  } = params;
  const [latestByPeer, setLatestByPeer] = useState<Record<string, LatestPeer>>({});
  /** Моя метка readThrough с документа треда (синхрон между устройствами). */
  const [myReadThroughByPeer, setMyReadThroughByPeer] = useState<Record<string, number>>({});
  /** Первый снимок документа треда получен — иначе readThrough неизвестен и точку не показываем (избегаем мигания). */
  const [threadMetaReadyByPeer, setThreadMetaReadyByPeer] = useState<Record<string, boolean>>({});
  /** Первый снимок по peer — только база, без звука. */
  const soundPrimedRef = useRef<Record<string, boolean>>({});
  const soundLastIdRef = useRef<Record<string, string | undefined>>({});
  const suppressSoundPeerRef = useRef(suppressIncomingSoundPeerToken);
  suppressSoundPeerRef.current = suppressIncomingSoundPeerToken;

  const getPeerNotifyMetaRef = useRef(getPeerNotifyMeta);
  const getPeerUidRef = useRef(getPeerUid);
  getPeerNotifyMetaRef.current = getPeerNotifyMeta;
  getPeerUidRef.current = getPeerUid;

  /**
   * Состав peer-токенов без привязки к порядку/ссылке на массив контактов (иначе лишние переподписки).
   */
  const peersKey = Array.from(new Set(peerTokens.filter(Boolean))).sort().join(PEER_KEY_SEP);

  const sortedUniquePeers = useMemo(() => {
    if (!peersKey) return [];
    return peersKey.split(PEER_KEY_SEP).filter(Boolean);
  }, [peersKey]);

  const prevMyInviteTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!uid || !myInviteToken) {
      prevMyInviteTokenRef.current = null;
      setLatestByPeer({});
      setMyReadThroughByPeer({});
      setThreadMetaReadyByPeer({});
      soundPrimedRef.current = {};
      soundLastIdRef.current = {};
      return;
    }

    const prevTok = prevMyInviteTokenRef.current;
    if (prevTok !== null && prevTok !== myInviteToken) {
      setLatestByPeer({});
      setMyReadThroughByPeer({});
      setThreadMetaReadyByPeer({});
    }
    prevMyInviteTokenRef.current = myInviteToken;

    const peers = peersKey ? peersKey.split(PEER_KEY_SEP).filter(Boolean) : [];
    const unsubs: Array<() => void> = [];

    for (const peer of peers) {
      if (peer === myInviteToken) continue;
      const threadId = p2pThreadId(myInviteToken, peer);
      const peerKey = peer;
      unsubs.push(
        subscribeP2PThreadMeta(threadId, (meta) => {
          const ts = computeMyReadThroughMessageTs(meta, myInviteToken, peerKey);
          setThreadMetaReadyByPeer((prev) => ({ ...prev, [peerKey]: true }));
          setMyReadThroughByPeer((prev) => {
            const prevVal = prev[peerKey] ?? 0;
            const merged = Math.max(prevVal, ts);
            return { ...prev, [peerKey]: merged };
          });
        })
      );

      unsubs.push(
        subscribeP2PLatestPeerMessage(
          threadId,
          uid,
          (msg) => {
            const next: LatestPeer = msg ? { id: msg.id, createdAtMs: msg.ts } : null;
            const primed = soundPrimedRef.current[peerKey];
            if (!primed) {
              soundPrimedRef.current[peerKey] = true;
              soundLastIdRef.current[peerKey] = msg?.id;
            } else {
              const prevId = soundLastIdRef.current[peerKey];
              const newId = msg?.id;
              const idChanged = newId !== undefined && newId !== prevId;
              const suppressForOpenChat = suppressSoundPeerRef.current === peerKey;
              if (idChanged && !suppressForOpenChat && msg && myInviteToken) {
                playIncomingMessageSound();
                const meta = getPeerNotifyMetaRef.current?.(peerKey);
                const custom = meta?.title?.trim();
                void (async () => {
                  if (!readChatBrowserNotifyEnabled()) return;
                  const body = await buildNotificationBodyFromRawForInbox({
                    uid,
                    myInviteToken,
                    peerInviteToken: peerKey,
                    raw: msg,
                    resolvePeerUid: async () => {
                      const fromDoc = getPeerUidRef.current?.(peerKey);
                      if (typeof fromDoc === 'string' && fromDoc.trim().length > 0) {
                        return fromDoc.trim();
                      }
                      const snap = await getDoc(doc(db, 'inviteLookup', peerKey));
                      const u = snap.exists()
                        ? (snap.data() as { uid?: string }).uid
                        : undefined;
                      return typeof u === 'string' && u ? u : null;
                    },
                    t: (k) => i18n.t(k),
                  });
                  showChatIncomingMessageNotification({
                    title:
                      custom && custom.length > 0 ? custom : i18n.t('chats.notificationDefaultTitle'),
                    body,
                    tag: `p2p-${threadId}-${newId}`,
                    iconUrl: meta?.iconUrl ?? undefined,
                  });
                })();
              }
              if (newId !== undefined) soundLastIdRef.current[peerKey] = newId;
              else delete soundLastIdRef.current[peerKey];
            }

            setLatestByPeer((prev) => ({
              ...prev,
              [peerKey]: next,
            }));
          },
          () => {
            delete soundPrimedRef.current[peerKey];
            delete soundLastIdRef.current[peerKey];
            setLatestByPeer((prev) => ({ ...prev, [peerKey]: null }));
          }
        )
      );
    }

    setLatestByPeer((prev) => {
      const merged = { ...prev };
      for (const k of Object.keys(merged)) {
        if (!peers.includes(k)) delete merged[k];
      }
      return merged;
    });

    setMyReadThroughByPeer((prev) => {
      const merged = { ...prev };
      for (const k of Object.keys(merged)) {
        if (!peers.includes(k)) delete merged[k];
      }
      return merged;
    });

    setThreadMetaReadyByPeer((prev) => {
      const merged = { ...prev };
      for (const k of Object.keys(merged)) {
        if (!peers.includes(k)) delete merged[k];
      }
      return merged;
    });

    return () => {
      unsubs.forEach((u) => u());
      soundPrimedRef.current = {};
      soundLastIdRef.current = {};
    };
  }, [uid, myInviteToken, peersKey]);

  const computedUnread = useMemo(() => {
    const out: Record<string, boolean> = {};
    if (!uid) return out;
    void readReceiptVersion;
    for (const peer of sortedUniquePeers) {
      const latest = latestByPeer[peer];
      if (!latest) {
        out[peer] = false;
        continue;
      }
      if (!threadMetaReadyByPeer[peer]) {
        out[peer] = false;
        continue;
      }
      const readMs = myReadThroughByPeer[peer] ?? 0;
      out[peer] = latest.createdAtMs > readMs;
    }
    return out;
  }, [
    uid,
    sortedUniquePeers,
    latestByPeer,
    myReadThroughByPeer,
    threadMetaReadyByPeer,
    readReceiptVersion,
  ]);

  const unreadFingerprint = useMemo(
    () => sortedUniquePeers.map((p) => `${p}:${computedUnread[p] ? 1 : 0}`).join(','),
    [sortedUniquePeers, computedUnread]
  );

  const computedUnreadRef = useRef(computedUnread);
  computedUnreadRef.current = computedUnread;

  const [displayUnreadByPeer, setDisplayUnreadByPeer] = useState<Record<string, boolean>>({});
  const displayUnreadRef = useRef(displayUnreadByPeer);
  displayUnreadRef.current = displayUnreadByPeer;
  const showUnreadTimerRef = useRef<Record<string, number>>({});
  const hideUnreadTimerRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const peers = sortedUniquePeers;
    const peerSet = new Set(peers);

    for (const peer of peers) {
      const want = !!computedUnreadRef.current[peer];

      if (want) {
        const hideT = hideUnreadTimerRef.current[peer];
        if (hideT != null) {
          clearTimeout(hideT);
          delete hideUnreadTimerRef.current[peer];
        }

        if (displayUnreadRef.current[peer]) continue;

        if (showUnreadTimerRef.current[peer] != null) continue;

        showUnreadTimerRef.current[peer] = window.setTimeout(() => {
          delete showUnreadTimerRef.current[peer];
          if (computedUnreadRef.current[peer]) {
            setDisplayUnreadByPeer((prev) => ({ ...prev, [peer]: true }));
          }
        }, UNREAD_DOT_SHOW_AFTER_MS);
        continue;
      }

      const showT = showUnreadTimerRef.current[peer];
      if (showT != null) {
        clearTimeout(showT);
        delete showUnreadTimerRef.current[peer];
      }

      if (!displayUnreadRef.current[peer]) continue;

      if (hideUnreadTimerRef.current[peer] != null) continue;

      hideUnreadTimerRef.current[peer] = window.setTimeout(() => {
        delete hideUnreadTimerRef.current[peer];
        if (!computedUnreadRef.current[peer]) {
          setDisplayUnreadByPeer((prev) => ({ ...prev, [peer]: false }));
        }
      }, UNREAD_DOT_HIDE_AFTER_MS);
    }

    for (const key of Object.keys(showUnreadTimerRef.current)) {
      if (!peerSet.has(key)) {
        clearTimeout(showUnreadTimerRef.current[key]!);
        delete showUnreadTimerRef.current[key];
      }
    }
    for (const key of Object.keys(hideUnreadTimerRef.current)) {
      if (!peerSet.has(key)) {
        clearTimeout(hideUnreadTimerRef.current[key]!);
        delete hideUnreadTimerRef.current[key];
      }
    }

    setDisplayUnreadByPeer((prev) => {
      let next: Record<string, boolean> | null = null;
      for (const k of Object.keys(prev)) {
        if (!peerSet.has(k)) {
          if (!next) next = { ...prev };
          delete next[k];
        }
      }
      return next ?? prev;
    });
  }, [unreadFingerprint, peersKey]);

  useEffect(() => {
    return () => {
      for (const t of Object.values(showUnreadTimerRef.current)) clearTimeout(t);
      for (const t of Object.values(hideUnreadTimerRef.current)) clearTimeout(t);
      showUnreadTimerRef.current = {};
      hideUnreadTimerRef.current = {};
    };
  }, []);

  return displayUnreadByPeer;
}
